// src/components/billCategories.js
// The quality-of-life category taxonomy shared by the Analysis tab (renamed
// from "Prediction") and the Bills Passed tab (city tenants). Extracted from
// AnalysisTab so both views
// classify a bill identically — a category deep-link from one tab must land on
// the same bucket in the other.

// Opens a URL as a real new browser TAB, not a chromeless popup window.
// window.open(url, '_blank', '<any feature string>') — even just 'noopener' —
// makes some browsers (Firefox, some Chromium builds) treat the call as a
// popup request and hide the address bar / tab strip. A real <a target="_blank">
// click never does this; it's how every ordinary link opens. rel="noopener
// noreferrer" gets the same security property (the new tab can't reach back
// into window.opener) without the popup side effect.
export function openInNewTab(url) {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/* ── Category taxonomy (order = tie-break priority) ── */
export const CATEGORIES = [
  {
    id: 'housing',
    label: 'Housing & Affordability',
    hint: 'Median rent, home prices, rent-to-income ratio, cost of living',
    keywords: [
      'housing', 'rent', 'tenant', 'eviction', 'homeless', 'shelter',
      'affordab', 'inclusionary', 'landlord', 'dwelling', 'residential',
      'condominium', 'zoning', 'land use', 'development agreement',
      'foreclosure', 'supportive housing',
    ],
  },
  {
    id: 'transportation',
    label: 'Transportation & Mobility',
    hint: 'Commute time, transit access/quality, congestion, walkability/bikeability',
    keywords: [
      'transit', 'muni', 'bart', 'traffic', 'bike', 'bicycle', 'pedestrian',
      'parking', 'transportation', 'congestion', 'walkab', 'scooter',
      'vision zero', 'bus ', 'rail', 'ferry', 'taxi', 'rideshare', 'e-bike',
    ],
  },
  {
    id: 'safety',
    label: 'Safety',
    hint: 'Violent crime rate, property crime rate',
    keywords: [
      'police', 'crime', 'sheriff', 'public safety', 'violence', 'theft',
      'firearm', 'gun', 'fireworks', 'gang', 'burglary', 'assault',
      'law enforcement', 'first responder', 'fire department', 'firefighter',
      'emergency response', '911',
    ],
  },
  {
    id: 'education',
    label: 'Education',
    hint: 'School quality/test scores, educational attainment',
    keywords: [
      'school', 'education', 'student', 'teacher', 'curriculum', 'tuition',
      'college', 'university', 'childcare', 'child care', 'preschool',
      'after-school', 'library',
    ],
  },
  {
    id: 'economy',
    label: 'Jobs & Economy',
    hint: 'Unemployment rate, job growth, median wages, economic diversity',
    keywords: [
      'employment', 'job', 'wage', 'workforce', 'small business', 'economic',
      'economy', 'labor', 'union', 'merchant', 'commerce', 'downtown recovery',
      'vacancy', 'tourism', 'payroll', 'cannabis', 'gross receipts',
      'tax credit',
    ],
  },
  {
    id: 'health',
    label: 'Health',
    hint: 'Healthcare access, life expectancy, health outcomes',
    keywords: [
      'health', 'hospital', 'clinic', 'mental', 'behavioral', 'patient',
      'overdose', 'treatment', 'medicaid', 'medi-cal', 'nurse', 'epidemic',
      'public health', 'drug program', 'wellness', 'medical',
    ],
  },
  {
    id: 'environment',
    label: 'Environment',
    hint: 'Air quality, parks/green space, climate risk',
    keywords: [
      'environment', 'climate', 'air quality', 'emission', 'green space',
      'recycling', 'waste', 'sustainab', 'sea level', 'pollution', 'wildlife',
      'coastal', 'tree', 'habitat', 'dredging',
    ],
  },
  {
    id: 'neighborhood',
    label: 'Neighborhood & Amenities',
    hint: 'Access to stores, parks, cultural/recreational options',
    keywords: [
      'park', 'recreation', 'cultural', 'arts', 'museum', 'festival',
      'neighborhood', 'community center', 'retail', 'grocery', 'plaza',
      'landmark', 'heritage', 'mural', 'event', 'entertainment', 'nightlife',
      'benefit district',
    ],
  },
  {
    id: 'infrastructure',
    label: 'Infrastructure',
    hint: 'Road quality, broadband, utilities reliability',
    keywords: [
      'road', 'street repair', 'repav', 'pothole', 'broadband', 'utility',
      'utilities', 'sewer', 'water system', 'power', 'grid',
      'capital improvement', 'construction', 'maintenance', 'bridge',
      'tunnel', 'infrastructure', 'striping', 'signage',
    ],
  },
  {
    id: 'equity',
    label: 'Equity / Opportunity',
    hint: 'Income inequality, poverty rate, access across groups',
    keywords: [
      'equity', 'inequality', 'poverty', 'discrimination', 'inclusion',
      'accessibility', 'ada', 'immigrant', 'refugee', 'reparation',
      'civil rights', 'low-income', 'underserved', 'disparities',
      'food security', 'safety net',
    ],
  },
];

export const OTHER = {
  id: 'other',
  label: 'Governance & Other',
  hint: 'Appointments, minutes, contracts, budget process, ceremonial items',
  keywords: [],
};

// Votings.category → our category id, used as a fallback when an issue's
// text matches no keywords.
export const VOTE_CATEGORY_HINTS = {
  'Land Use, Housing & Development': 'housing',
  'Transportation & Infrastructure': 'transportation',
  'Public Safety': 'safety',
  'Health & Human Services': 'health',
  'Environment & Public Utilities': 'environment',
};

// Keywords that are deliberate stems ("affordab" matches affordable/
// affordability) — matched with a leading word boundary only. Everything else
// gets boundaries on both sides: bare substring matching produced real
// misclassifications ("street" contains "tree" → Environment, "transitional"
// contains "transit" → Transportation).
const STEM_KEYWORDS = new Set(['affordab', 'walkab', 'sustainab', 'repav']);
const kwRegexCache = new Map();
const kwRegex = (kw) => {
  let re = kwRegexCache.get(kw);
  if (!re) {
    const esc = kw.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    re = new RegExp(`\\b${esc}${STEM_KEYWORDS.has(kw) ? '' : '\\b'}`);
    kwRegexCache.set(kw, re);
  }
  return re;
};

export function classify(text, hintCategory = null) {
  const t = ` ${String(text || '').toLowerCase()} `;
  let best = null;
  let bestScore = 0;
  for (const cat of CATEGORIES) {
    let score = 0;
    for (const kw of cat.keywords) {
      if (kwRegex(kw).test(t)) score += 1;
    }
    if (score > bestScore) {
      best = cat;
      bestScore = score;
    }
  }
  if (best) return best.id;
  if (hintCategory && VOTE_CATEGORY_HINTS[hintCategory]) {
    return VOTE_CATEGORY_HINTS[hintCategory];
  }
  return OTHER.id;
}

// "10-0" → passed when yes > no.
export const votePassed = (outcome) => {
  const m = String(outcome || '').match(/^(\d+)\s*-\s*(\d+)$/);
  if (!m) return false;
  return Number(m[1]) > Number(m[2]);
};
